// CONTRACT:I4-POLICY-DECISION.1.0
/** The pure ABAC evaluator. Mirrors TDFLite's evaluateGroup() (app.js:611-685) and
 *  the authoritative Go evaluator (evaluate.go:213-416) so the LOCAL PDP makes the
 *  SAME decision the KAS would. Deterministic, case-insensitive, no I/O.
 *
 *  Per attribute:
 *    hierarchy → permit iff user's value index ≤ the lowest (highest-level) bound
 *                index. Index 0 = highest (TOP_SECRET). i.e. READ-DOWN: a SECRET
 *                subject reads CONFIDENTIAL; is denied TOP_SECRET.
 *    allOf     → subject must hold EVERY bound value.
 *    anyOf     → subject must hold AT LEAST ONE bound value.
 *  Across attributes: releasable iff EVERY bound attribute is satisfied (AND).
 *  Fail-closed: an unknown attribute / value-not-in-set / no held value ⇒ deny. */

import type {
  AttributeDef,
  AttributeVerdict,
  Decision,
  PolicyBundle,
  SubjectClaims,
} from "./types.ts";
import { parseAttrFqn } from "./fqn.ts";

const lc = (s: string): string => String(s).toLowerCase();

function userValuesFor(claims: SubjectClaims, attr: string): string[] {
  const claim = claims[attr];
  if (Array.isArray(claim)) return claim.map(lc);
  if (claim != null && claim !== "") return [lc(String(claim))];
  return [];
}

function evaluateGroup(
  def: AttributeDef,
  dataValues: string[],
  userValues: string[],
): AttributeVerdict {
  const base = {
    attr: def.name,
    rule: def.rule,
    dataValues,
    userValues,
  } as const;

  switch (def.rule) {
    case "hierarchy": {
      const definedIdx = (v: string): number =>
        def.values.findIndex((x) => lc(x) === lc(v));
      const userIdx = userValues.length ? definedIdx(userValues[0]) : -1;
      // Lowest data index = highest level among the bound values.
      let minDataIdx = Infinity;
      for (const v of dataValues) {
        const i = definedIdx(v);
        if (i >= 0 && i < minDataIdx) minDataIdx = i;
      }
      if (userIdx < 0 || minDataIdx === Infinity) {
        return { ...base, satisfied: false, reason: "value not in attribute set" };
      }
      const ok = userIdx <= minDataIdx; // read-down
      return {
        ...base,
        satisfied: ok,
        reason: ok
          ? `holds ${userValues[0].toUpperCase()} ≥ ${def.values[minDataIdx].toUpperCase()}`
          : `holds ${userValues[0].toUpperCase()} < ${def.values[minDataIdx].toUpperCase()}`,
      };
    }
    case "allOf": {
      const missing = dataValues.filter((v) => !userValues.includes(lc(v)));
      const ok = missing.length === 0;
      return {
        ...base,
        satisfied: ok,
        reason: ok
          ? `holds all ${dataValues.length} required`
          : `lacks ${missing.map((v) => v.toUpperCase()).join(", ")}`,
      };
    }
    case "anyOf": {
      const matches = dataValues.filter((v) => userValues.includes(lc(v)));
      const ok = matches.length > 0;
      return {
        ...base,
        satisfied: ok,
        reason: ok
          ? `holds ${matches.map((v) => v.toUpperCase()).join(", ")}`
          : `holds none of ${dataValues.map((v) => v.toUpperCase()).join(", ")}`,
      };
    }
    default:
      return { ...base, satisfied: false, reason: `unknown rule "${def.rule}"` };
  }
}

/** Decide whether a subject may SEE an object, given the object's bound attribute
 *  FQNs, the policy bundle, and the subject's held claims. */
export function evaluateAbac(
  boundFqns: string[],
  bundle: PolicyBundle,
  claims: SubjectClaims,
): Decision {
  // Group bound FQNs by attribute name; collect (lowercased) data values.
  const groups = new Map<string, string[]>();
  const order: string[] = [];
  for (const fqn of boundFqns) {
    const parsed = parseAttrFqn(fqn);
    if (!parsed) {
      // Fail-closed: an unparseable binding denies the whole object.
      return {
        permit: false,
        reason: `unparseable bound attribute: ${fqn}`,
        verdicts: [
          { attr: fqn, rule: "?", satisfied: false, reason: "unparseable FQN", dataValues: [], userValues: [] },
        ],
      };
    }
    if (!groups.has(parsed.name)) {
      groups.set(parsed.name, []);
      order.push(parsed.name);
    }
    groups.get(parsed.name)!.push(lc(parsed.value));
  }

  // No bound attributes ⇒ unbound object ⇒ visible to all.
  if (order.length === 0) {
    return { permit: true, reason: "no bound attributes", verdicts: [] };
  }

  const verdicts: AttributeVerdict[] = order.map((name) => {
    const def = bundle.attributes.find((a) => lc(a.name) === lc(name));
    const dataValues = groups.get(name)!;
    if (!def) {
      // Fail-closed: unknown attribute denies.
      return { attr: name, rule: "?", satisfied: false, reason: "unknown attribute", dataValues, userValues: [] };
    }
    return evaluateGroup(def, dataValues, userValuesFor(claims, def.name));
  });

  const permit = verdicts.every((v) => v.satisfied);
  const fail = verdicts.find((v) => !v.satisfied);
  return {
    permit,
    reason: permit
      ? "permit — " + verdicts.map((v) => v.reason).join("; ")
      : `deny — ${fail!.attr}: ${fail!.reason}`,
    verdicts,
  };
}
